import { Cache } from 'memory-cache';
import { PriorityTaskQueue } from 'owa-task-queue';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { TokenRequestParams } from 'owa-tokenprovider-utils';
import type { TokenCallbackType } from '../schema/TokenCallbackType';
import type { ITokenQueue } from './ITokenQueue';
import {
    MAX_NUM_OF_GET_ACCESS_TOKEN_REQUESTS,
    DELAY_FOR_GET_ACCESS_TOKEN_REQUESTS,
    TIMEOUT_FOR_TOKEN_REQUEST_PROMISE,
} from '../const/constants';
import type { LogStartPerformanceUsage } from 'owa-analytics-start';
import { isBootFeatureEnabled } from 'owa-metatags';
import { getTokenRequestPriority } from './getTokenRequestPriority';

export class TokenQueue implements ITokenQueue {
    private requestPromiseCache: Cache<string, Promise<TokenResponse>>;
    private tokenQueue: PriorityTaskQueue<TokenResponse>;

    constructor() {
        this.tokenQueue = new PriorityTaskQueue<TokenResponse>({
            maxParallelTasks: MAX_NUM_OF_GET_ACCESS_TOKEN_REQUESTS,
            taskDelay: DELAY_FOR_GET_ACCESS_TOKEN_REQUESTS,
            isUserBlocking: isBootFeatureEnabled('perf-prioritize-tokenFetch'),
        });
        this.requestPromiseCache = new Cache();
    }

    /**
     * This provides caching to store promise to token request mapping (both business and consumer scenarios).
     * Used when similar requests are made for the same cacheKey, this cache
     * is looked up to return an existing promise to the caller.
     */
    public enqueueRequestIfNotCached(
        cacheKey: string,
        authParams: TokenRequestParams,
        authTaskCallback: TokenCallbackType,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const enqueueTime = Date.now();

        const cachedRequestPromise = this.getCachedPromise(cacheKey);

        if (cachedRequestPromise) {
            perfLog.addCheckpoint('CachedPromise');
            perfLog.addCustomProperty('DedupRole', 'follower');
            // Preserve promise identity for callers that dedup on it; attach the
            // wait-time side-effect without wrapping the returned promise.
            cachedRequestPromise
                .finally(() => {
                    perfLog.addCustomProperty('TotalWaitMs', Math.max(0, Date.now() - enqueueTime));
                })
                // The caller handles the original promise; suppress rejection only on this side chain.
                .catch(() => {});
            return cachedRequestPromise;
        }

        perfLog.addCustomProperty('DedupRole', 'leader');
        const priority = getTokenRequestPriority(authParams.resource);
        perfLog.addCustomProperty('QueuePriority', priority);
        const requestPromise: Promise<TokenResponse> = this.tokenQueue.add(() => {
            const taskStart = Date.now();
            perfLog.addCustomProperty('QueueSchedulingMs', Math.max(0, taskStart - enqueueTime));
            return authTaskCallback(authParams, perfLog).finally(() => {
                const taskEnd = Date.now();
                perfLog.addCustomProperty('TokenFetchMs', Math.max(0, taskEnd - taskStart));
                perfLog.addCustomProperty('TotalWaitMs', Math.max(0, taskEnd - enqueueTime));
            });
        }, priority);
        this.putPromiseInCache(cacheKey, requestPromise);
        perfLog.addCheckpoint('PromiseAddedToAuthCache');
        return requestPromise;
    }

    /**
     * Gets the cached promise for the "already-seen" cacheKey.
     */
    public getCachedPromise(cacheKey: string): Promise<TokenResponse> | null {
        return this.requestPromiseCache.get(cacheKey);
    }

    /**
     * Deletes the cached promise for the "already-seen" cacheKey, when the Promise has been resolved
     */
    public deleteCachedPromise(cacheKey: string): boolean {
        return this.requestPromiseCache.del(cacheKey);
    }

    /**
     * Puts the duplicate request to the PromiseCache
     */
    public putPromiseInCache(cacheKey: string, promise: Promise<TokenResponse>): void {
        this.requestPromiseCache.put(cacheKey, promise, TIMEOUT_FOR_TOKEN_REQUEST_PROMISE);
    }
}
